/**
 * ListsHub - Central de Importação de Listas
 *
 * Página unificada que reúne em um único lugar:
 *   - Aba "Contatos" (antiga página /tenant/lists)
 *   - Aba "Negócios" (antiga página /tenant/crm/deals/import)
 *
 * A aba inicial é definida por:
 *   1. Query string ?tab=contacts|deals
 *   2. Prop `defaultTab` (vinda da rota)
 *   3. Primeira aba que o usuário tem permissão de visualizar
 *
 * Segurança:
 *   - Cada aba checa permissão granular:
 *       * Contatos -> 'campaigns'
 *       * Negócios -> 'crm_deals_import'
 *   - Abas sem permissão são ocultadas.
 *   - Se o usuário não tem permissão para nenhuma das duas, redireciona
 *     para /access-denied.
 *
 * NOTA: as RPCs (get_campaign_lists, list_deal_import_jobs, etc.) já
 * validam permissão server-side; este componente é apenas a camada de UX.
 */

import { useEffect, useMemo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useReferralModuleAvailable } from '@/hooks/useReferralModuleAvailable';
import { Skeleton } from '@/components/ui/skeleton';
import { ListsHubTabs, type ListsHubTabKey, type ListsHubTabDef } from '@/components/lists/ListsHubTabs';
import ContactListsTab from '@/components/lists/ContactListsTab';
import DealListsTab from '@/components/lists/DealListsTab';
import AffiliateListsTab from '@/components/lists/AffiliateListsTab';
import CustomerListsTab from '@/components/lists/CustomerListsTab';

interface ListsHubProps {
  /** Aba sugerida pela rota (ex: /tenant/lists -> 'contacts'; /tenant/crm/deals/import -> 'deals') */
  defaultTab?: ListsHubTabKey;
}

export default function ListsHub({ defaultTab = 'contacts' }: ListsHubProps) {
  const { loading, canView } = usePermissions();
  const { currentTenant } = useAuth();
  const { isAvailable: referralEnabled } = useReferralModuleAvailable();
  const [searchParams, setSearchParams] = useSearchParams();

  // Flags de módulos do tenant — abas dependem tanto da permissão quanto do
  // módulo estar habilitado (evita exibir Negócios/Clientes sem CRM, etc.)
  const crmEnabled = currentTenant?.crm_enabled ?? false;

  // Calcula abas visíveis conforme permissões + módulos habilitados no tenant
  const availableTabs: ListsHubTabDef[] = useMemo(() => {
    const tabs: ListsHubTabDef[] = [];
    // Cada aba usa uma chave de permissão dedicada (lists_*), permitindo que
    // supervisores recebam acesso individual a cada aba via Perfis de Permissão.
    if (canView('lists_contacts')) {
      tabs.push({ key: 'contacts', label: 'Contatos', color: 'hsl(var(--primary))' });
    }
    if (canView('lists_deals') && crmEnabled) {
      tabs.push({ key: 'deals', label: 'Negócios', color: 'hsl(var(--primary))' });
    }
    if (canView('lists_customers') && crmEnabled) {
      tabs.push({ key: 'customers', label: 'Clientes', color: 'hsl(var(--primary))' });
    }
    if (canView('lists_affiliates') && referralEnabled) {
      tabs.push({ key: 'affiliates', label: 'Afiliados', color: 'hsl(var(--primary))' });
    }
    return tabs;
  }, [canView, crmEnabled, referralEnabled]);

  // Resolve aba ativa: query > defaultTab > primeira disponível
  const requestedTab = (searchParams.get('tab') as ListsHubTabKey | null) ?? defaultTab;
  const activeTab: ListsHubTabKey | null = useMemo(() => {
    if (availableTabs.length === 0) return null;
    const ok = availableTabs.find((t) => t.key === requestedTab);
    return ok ? ok.key : availableTabs[0].key;
  }, [availableTabs, requestedTab]);

  // Sincroniza ?tab= quando precisamos forçar fallback (sem disparar replace desnecessário)
  useEffect(() => {
    if (!activeTab) return;
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleChangeTab = (key: ListsHubTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  };

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Sem permissão para nenhuma aba => acesso negado
  if (availableTabs.length === 0 || !activeTab) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header global da Central de Importação de Listas */}
      <div className="flex flex-col gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Importação de Listas</h1>
          <p className="text-xs text-muted-foreground">
            Central unificada para gerenciar listas de contatos e importações de negócios.
          </p>
        </div>
        <ListsHubTabs tabs={availableTabs} value={activeTab} onChange={handleChangeTab} />
      </div>

      {/* Conteúdo da aba ativa */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'contacts' && <ContactListsTab />}
        {activeTab === 'deals' && <DealListsTab />}
        {activeTab === 'customers' && <CustomerListsTab />}
        {activeTab === 'affiliates' && <AffiliateListsTab />}
      </div>
    </div>
  );
}
